import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RelatedProductsService } from '@df/catalog-ui/related-products/related-products.service';
import { ZippyEventType, subject } from '@df/legacy/ui/zippy/zippy.abstract';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import find from 'lodash-es/find';
import orderBy from 'lodash-es/orderBy';
import { RelatedProductsComponent } from '../../related-products/related-products.component';
import { AbstractProductCmsComponent } from '../abstract-product-cms.component';
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace NCmsRelatedProducts {
    export interface IComponentData extends NICmsContentComponent.IData {
        sections: ISection[];
    }
    export interface ISection {
        /**
         * Section title
         */
        title: string;
        /**
         * Section copy
         */
        copy?: string;
        /**
         * Max items qty
         */
        limit: number;
        /**
         * Engines list
         */
        engines: IEngine[];
    }
    export interface IEngine {
        type: string;
    }
    export interface ISections {
        section: ISection;
        position: number;
        visible: boolean;
    }
}

let uid = 0;
@Component({
    selector: 'cms-related-products',
    templateUrl: './cms-related-products.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, RelatedProductsComponent, ZippyModule, ICmsClientModule]
})
@ICmsComponent<CmsRelatedProductsComponent>({
    id: 'related-products',
    name: 'Related products',
    tags: ['product page', 'listing', 'carousel'],
    onDemandOnly: true,
    fields: [
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'sections',
            title: 'Sections',
            itemName: 'Section',
            itemNameField: 'title',
            defaultSize: 1,
            fields: [
                <NICmsContentField.IField>{
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Section title',
                    default: 'You may also like',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'copy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Copy',
                    default: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean pellentesque commodo pulvinar.',
                    required: false
                },
                <NICmsContentField.IField>{
                    key: 'limit',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Max products qty',
                    required: true,
                    limit: 4
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'engines',
                    title: 'Engines',
                    itemName: 'Engine',
                    itemNameField: 'id',
                    required: true,
                    defaultSize: 1,
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            key: 'type',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Type',
                            required: true,
                            options: () => Object.keys(RelatedProductsService.engines).map(i => ({ key: i, value: i, title: i })),
                            default: 'category'
                        }
                    ]
                }
            ]
        }
    ]
})
export class CmsRelatedProductsComponent extends AbstractProductCmsComponent<NCmsRelatedProducts.IComponentData> {
    readonly uid = `offCanvasMenu.${uid++}`;

    private _sections: NCmsRelatedProducts.ISections[] = [];
    /**
     * List of visible section (ones that do have any products to show)
     */
    visibleSections: NCmsRelatedProducts.ISection[] = [];
    /**
     * Retrieves related product component options
     */
    getOptions(section: NCmsRelatedProducts.ISection) {
        return { engines: section.engines.map(i => i.type) };
    }
    /**
     * If componentn uses multiple sections
     */
    get hasManySections(): boolean {
        return this.visibleSections.length > 1;
    }

    onPrinted(hasModels: boolean, section: NCmsRelatedProducts.ISection, position = 0) {
        const exists = find(this._sections, (item: NCmsRelatedProducts.ISections) => item.section === section);

        if (exists) {
            const wasVisible = exists.visible;
            exists.visible = hasModels;

            if (wasVisible !== hasModels) {
                this.visibleSections = orderBy(this._sections, 'position').map(item => item.section);
                this.detectChanges();
                this.timeoutService.setTimeout(() =>
                    subject.next({ type: ZippyEventType.openAny, id: { name: '', group: this.uid, isGroup: true } })
                );
            }
        } else if (hasModels) {
            this._sections.push({
                section,
                position,
                visible: hasModels
            });

            this.visibleSections = orderBy(this._sections, 'position').map(item => item.section);
            this.detectChanges();
            this.timeoutService.setTimeout(() =>
                subject.next({ type: ZippyEventType.openAny, id: { name: '', group: this.uid, isGroup: true } })
            );
        }
    }

    isVisible(section: NCmsRelatedProducts.ISection): boolean {
        return this.visibleSections.includes(section);
    }
}
